import type { MessageFormatElement } from '@formatjs/icu-messageformat-parser';
import { trackError } from '@atlassian/help-center-common-util/analytics';
import { getLanguageFromLocale } from '@atlassian/help-center-common-util/locale-data';
import { reportError } from '@atlassian/help-center-common-util/sentry';

// We need to explicitly type these as inferred types are 'any'
type EditorMessages =
	| Record<string, string>
	| Record<string, MessageFormatElement[]>
	| Record<string, never>;

interface EditorModule {
	default: EditorMessages;
}

type EditorMessageModule = EditorModule | undefined;

export const shouldIgnoreError = (locale: string) => {
	const unavailableTranslationsForLocales = {
		'editor-common': ['ro', 'is'],
	};

	for (const [, value] of Object.entries(unavailableTranslationsForLocales)) {
		return value.includes(locale);
	}

	return false;
};

/**
 * Atlaspack-only loader for the editor translation chunks. Lives in a
 * sibling file so its `import('@atlaskit/<pkg>/i18n/<locale>')` calls are
 * never parsed by webpack — webpack inlines `__ATLASPACK__` as `false`,
 * eliminates the `require` call below at parse time, and never tries to
 * resolve any of those specifiers. (Webpack's platform-resolver currently
 * maps `@atlaskit/<pkg>` to `<pkg>/src/index.ts` and then prefix-matches
 * any subpath onto that file, so those literal imports would fail to
 * resolve on webpack — keeping them out of webpack's module graph entirely
 * is the cleanest fix.)
 *
 * The variable below is populated only when `__ATLASPACK__ === true`. On
 * webpack it remains `null` and the `useLiteralLookup` check inside
 * `importLocaleMessages` is forced to `false`, so the legacy
 * template-literal branch always runs.
 */
let atlaspackImporter: ((locale: string) => Promise<EditorMessageModule>[]) | null = null;

if (typeof __ATLASPACK__ !== 'undefined' && __ATLASPACK__) {
	// Static `require()` inside a literal-constant `if (false)` branch is
	// tree-shaken by webpack at parse time (the dependency is never added
	// to the module graph). Atlaspack inlines `__ATLASPACK__` as `true`,
	// so the file is loaded normally. In Jest (where `__ATLASPACK__` is
	// undefined), the require is skipped and the legacy branch runs.

	const atlaspackModule = require('./editor-translation-util.atlaspack') as {
		getEditorTranslationImports: (locale: string) => Promise<EditorMessageModule>[];
	};
	atlaspackImporter = atlaspackModule.getEditorTranslationImports;
}

export const importLocaleMessages = async (locale: string) => {
	const errors: Record<string, string>[] = [];

	// Editor translations have two viable import strategies depending on the
	// bundler:
	//
	//  ┌─ Atlaspack (`BABEL_ENV === 'atlaspack'` or `'atlaspack-ssr'`) ───────
	//  │   Use the literal `EDITOR_TRANSLATION_IMPORTERS` lookup table.
	//  │   Mirrors the canonical Confluence pattern in
	//  │   `confluence/next/packages/i18n/src/messages.ts`. Atlaspack only
	//  │   emits async bundles for fully static import specifiers, so a
	//  │   literal lookup is the only form that works there — with the
	//  │   template-literal form below, atlaspack cannot statically discover
	//  │   these files and the editor hangs in an infinite load.
	//  │
	//  └─ Webpack (anything else) ───────────────────────────────────────────
	//      Keep using the legacy template-literal `import()` with deep
	//      relative paths into the platform monorepo, which webpack resolves
	//      via its require.context-style directory glob. Webpack's
	//      platform-resolver maps `@atlaskit/<pkg>` to the package's
	//      `src/index.ts` file and then appends any subpath after that
	//      file path, so `@atlaskit/<pkg>/i18n/<locale>` resolves to
	//      `.../src/index.ts/i18n/<locale>` and breaks. Until we change
	//      the platform-resolver, webpack must keep the relative-path
	//      form.
	//
	// Build-time discriminator: `__ATLASPACK__` is a global injected at build
	// time — `true` by atlaspack (via `@atlaspack/transformer-js`'s `define`
	// config) and `false` by webpack (via `webpack.DefinePlugin`). It collapses
	// to a literal boolean in compiled output, so the unused branch is
	// dropped by DCE and there are no runtime `process` lookups. Defaults to
	// `false` in environments that don't inject it (e.g. Jest / Node).
	// `atlaspackImporter` is only populated when `__ATLASPACK__ === true`
	// (see the `require()` at the top of this file). On webpack it's always
	// `null` because DCE eliminates the `require()` call, so the legacy
	// template-literal branch always runs.
	const useLiteralLookup = atlaspackImporter !== null;

	const imports =
		useLiteralLookup && atlaspackImporter
			? atlaspackImporter(locale)
			: [
					import(
						/* webpackChunkName: "editor-core-i18n" */ `../../../../platform/packages/editor/editor-core/src/i18n/${locale}`
					),
					import(
						/* webpackChunkName: "emoji-i18n" */ `../../../../platform/packages/elements/emoji/src/i18n/${locale}`
					),
					import(
						/* webpackChunkName: "editor-common-i18n" */ `../../../../platform/packages/editor/editor-common/src/i18n/${locale}`
					),
					import(
						/* webpackChunkName: "media-ui-i18n" */ `../../../../platform/packages/media/media-ui/src/i18n/${locale}`
					),
					import(
						/* webpackChunkName: "smart-card-i18n" */ `../../../../platform/packages/linking-platform/smart-card/src/i18n/${locale}`
					),
				];

	const modulesPromises = imports.map(async (importPromise) => {
		try {
			// Suppressing existing violation. Please fix this.
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			const module: EditorMessageModule = await importPromise;
			return module;
		} catch (error) {
			return Promise.reject(error);
		}
	});

	try {
		const modules = await Promise.allSettled(modulesPromises);
		const allMessages: EditorMessages = modules.reduce((messages, module) => {
			let moduleMessages;
			if (module.status === 'fulfilled') {
				moduleMessages = module.value?.default ? module.value?.default : {};
			} else if (module.status === 'rejected') {
				if (module.reason) {
					// Suppressing existing violation. Please fix this.
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					const moduleName =
						// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
						module.reason?.moduleName ? module.reason?.moduleName : 'unknownModule';

					// Suppressing existing violation. Please fix this.
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
					errors.push({ [moduleName]: module.reason?.message ? module.reason?.message : '' });
				} else {
					errors.push({ unknownModule: 'reason unknown' });
				}
			}

			return { ...messages, ...moduleMessages };
		}, {});

		if (errors.length > 0 && !shouldIgnoreError(locale)) {
			trackError(
				'editor.translations.failed.to.load',
				{ message: JSON.stringify(errors) },
				`Editor translations failed to load for locale ${locale}`,
			);
		}

		return Promise.resolve(allMessages);
	} catch (error) {
		reportError(`Error in resolving editor translations for ${locale}`, {
			// Suppressing existing violation. Please fix this.
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,  @typescript-eslint/no-unsafe-member-access
			message: error?.message ? error?.message : '',
		});

		return Promise.resolve({});
	}
};

export const getNormalizedLocale = (locale: string) => {
	let normalizedLocale = locale;

	if (locale === 'no-NO') {
		normalizedLocale = 'nb';
	} else if (locale === 'pt-BR') {
		normalizedLocale = 'pt_BR';
	} else if (locale === 'pt-PT') {
		normalizedLocale = 'pt_PT';
	} else if (locale === 'en-GB') {
		normalizedLocale = 'en_GB';
	} else {
		normalizedLocale = getLanguageFromLocale(locale);
	}

	return normalizedLocale;
};
